'use client';

import { Badge, DrawerCollapsible, Text } from '@terra/ui-design-system';
import type { AiComponentDto } from '@terra/shared-contracts-ai-applications';
import { HStack, VStack } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import {
  getMcpComponentAccessScope,
  getMcpComponentIdentityAccess,
  isMcpAssetComponentType,
  resolveMcpIdentityAccessPresentation,
} from '../utils/aiComponentMetadata';

type McpIdentityAccessDetailsProps = {
  component: AiComponentDto;
};

export function McpIdentityAccessDetails({
  component,
}: McpIdentityAccessDetailsProps) {
  const t = useTranslations('aiApplications.componentDrawer.mcp');
  const accessLabels = {
    accessDenied: t('notListed'),
    call: t('accessCall'),
  };

  if (!isMcpAssetComponentType(component.type)) {
    return null;
  }

  const identityAccess = getMcpComponentIdentityAccess({
    metadata: component.metadata,
  });
  const accessScope = getMcpComponentAccessScope({
    metadata: component.metadata,
  });

  if (identityAccess.length === 0) {
    return null;
  }

  return (
    <DrawerCollapsible title={t('identityAccess')} dense defaultOpen>
      <VStack alignItems="stretch" gap={2}>
        {accessScope && identityAccess.length >= 2 ? (
          <HStack gap={2}>
            <Badge colorPalette={accessScope === 'ALL' ? 'green' : 'orange'}>
              {accessScope === 'ALL'
                ? t('accessAllIdentities')
                : t('accessSomeIdentities')}
            </Badge>
          </HStack>
        ) : null}
        {identityAccess.map((entry) => {
          const missingScopes = entry.missingScopes ?? [];
          const presentation = resolveMcpIdentityAccessPresentation({
            entry,
            labels: accessLabels,
          });

          return (
            <HStack
              key={entry.identityId}
              justifyContent="space-between"
              alignItems="center"
              gap={3}
            >
              <Text fontSize="sm" color="fg.default" truncate>
                {entry.label}
              </Text>
              <HStack gap={1.5} flexShrink={0}>
                <Badge colorPalette={presentation.colorPalette}>
                  {presentation.label}
                </Badge>
                {missingScopes.length > 0 ? (
                  <Badge colorPalette="orange" variant="outline">
                    {t('missingScopes', {
                      scopes: missingScopes.join(', '),
                    })}
                  </Badge>
                ) : null}
              </HStack>
            </HStack>
          );
        })}
      </VStack>
    </DrawerCollapsible>
  );
}
